import React from 'react';
import { Box } from '@material-ui/core';
import { DataGrid, GridCellParams, GridColumns } from '@material-ui/data-grid';
import { makeStyles } from '@material-ui/styles';
import { unpackToDateTime } from '@campfire/hot-date';
import { CommonRequiringActionWidgetProps } from '../../shared';
import { useCampfireQuery } from '../../../../../../../../global/network/useCampfireQuery';
import { VolunteerProfile } from '../../../../../../../../common/AvatarName';
import { GET_TABLE_WIDGET_ANNIVERSARIES } from './anniversaries-table-widget-model.gql';
import { GetTableWidgetAnniversaries } from './__generated__/GetTableWidgetAnniversaries';

const useStyles = makeStyles({
  root: {
    '& .past-anniversary': {
      backgroundColor: '#f5f5f5',
    },
  },
});

export const VolunteerAnniversaryTableWidget = (props: CommonRequiringActionWidgetProps) => {
  const classes = useStyles();
  const { filters } = props;

  const { programIds, activityIds } = JSON.parse(filters) as { programIds: [string]; activityIds: [string] };

  const { data, loading } = useCampfireQuery<GetTableWidgetAnniversaries, any>(GET_TABLE_WIDGET_ANNIVERSARIES, {
    options: {
      variables: {
        programIds: programIds.length > 0 && !programIds.includes('all') ? programIds : null,
        activityIds: activityIds.length > 0 && !activityIds.includes('all') ? activityIds : null,
      },
    },
  });

  const anniversaries =
    data?.vm.profileAnniversaries
      .map((anniversary) => {
        const volunteerDateTime = unpackToDateTime(anniversary.volunteer?.dateCreated);
        const volunteerDate = volunteerDateTime.toJSDate(); // Convert DateTime to JavaScript Date
        const today = new Date();
        const currentYear = today.getFullYear();

        // Calculate this year's anniversary
        const thisYearAnniversary = new Date(currentYear, volunteerDate.getMonth(), volunteerDate.getDate());

        // Calculate years of service (regardless of whether anniversary has passed)
        const yearsOfService = currentYear - volunteerDate.getFullYear();

        // Check if anniversary is today
        const isToday = thisYearAnniversary.toDateString() === today.toDateString();

        // Check if this year's anniversary has passed
        const hasPassed = thisYearAnniversary < today && !isToday;

        return {
          id: anniversary.profileId,
          profileId: anniversary.profileId,
          preferredName: anniversary.preferredName ?? anniversary.firstName,
          firstName: anniversary.firstName,
          lastName: anniversary.lastName,
          volunteerDateCreated: volunteerDate,
          thisYearAnniversary: thisYearAnniversary,
          yearsOfService: yearsOfService,
          isToday: isToday,
          hasPassed: hasPassed,
          userId: anniversary.userId,
        };
      })
      .sort((a, b) => {
        // Sort by this year's anniversary, earliest to latest
        return a.thisYearAnniversary.getTime() - b.thisYearAnniversary.getTime();
      }) || [];

  const columns: GridColumns = [
    {
      field: 'volunteerName',
      headerName: 'Volunteer',
      flex: 1,
      disableColumnMenu: true,
      minWidth: 180,
      renderCell: (params: GridCellParams) => {
        const { row } = params;
        return (
          <VolunteerProfile
            inTable
            name={`${row.preferredName} ${row.lastName}`}
            lastName={row.firstName}
            profileId={row.profileId}
            preferredName={row.preferredName}
            userId={row.userId}
          />
        );
      },
    },
    {
      field: 'volunteerDateCreated',
      headerName: 'Anniversary Date',
      flex: 1,
      disableColumnMenu: true,
      minWidth: 170,
      renderCell: (params: GridCellParams) => {
        const { row } = params;
        const date = params.value as Date;
        const formattedDate = date.toLocaleDateString('en-US', {
          month: 'long',
          day: 'numeric',
          year: 'numeric',
        });

        return (
          <div>
            {row.isToday && (
              <div
                style={{
                  fontSize: '0.7rem',
                  color: '#666',
                  marginBottom: '1px',
                  lineHeight: '1.2',
                }}
              >
                Today{' '}
                <span role='img' aria-label='celebration'>
                  🎉
                </span>
              </div>
            )}
            <div style={{ lineHeight: '1.3' }}>{formattedDate}</div>
          </div>
        );
      },
    },
    {
      field: 'yearsOfService',
      headerName: 'Years of Service',
      flex: 1,
      disableColumnMenu: true,
      minWidth: 140,
      renderCell: (params: GridCellParams) => {
        const years = params.value as number;
        return `${years} year${years !== 1 ? 's' : ''}`;
      },
    },
  ];

  return (
    <Box width='100%' height='100%'>
      <DataGrid
        className={classes.root}
        columns={columns}
        rows={anniversaries}
        loading={loading}
        autoPageSize
        disableSelectionOnClick
        getRowClassName={(params) => (params.row.hasPassed ? 'past-anniversary' : '')}
      />
    </Box>
  );
};
